// End scene: shows the marketing message + CTAs in an HTML overlay.
// The Phaser canvas is left blank — everything visible is the overlay
// (pixel diorama + card).

import Phaser from 'phaser';
import { brand } from '../brand';
import { GameParams } from '../params';
import { RunState, elapsedSeconds } from '../systems/state';
import { track } from '../systems/analytics';
import { AUDIO_KEYS, playBgm, playSfx } from '../systems/audio';

const fmtTime = (sec: number) => {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

export class EndScene extends Phaser.Scene {
  constructor() {
    super('End');
  }
  init(_data: { params: GameParams; state: RunState }) {
    // nothing
  }

  create(data: { params: GameParams; state: RunState }) {
    this.cameras.main.setBackgroundColor(brand.bg);

    // Game-over stinger — fires on every losing outcome (health, timeout,
    // win-then-boss). Lives here rather than at the moment of death so it
    // doesn't overlap sfxCveHit on the killing blow.
    if (data.state.outcome !== 'boss-win') {
      playSfx(this, AUDIO_KEYS.sfxLose);
    }

    // Summary beat. Falls back to the loadscreen track if bgm_end.mp3
    // hasn't been dropped in yet (audio loader silently skips missing files).
    const endKey = this.cache.audio.exists(AUDIO_KEYS.bgmEnd)
      ? AUDIO_KEYS.bgmEnd
      : AUDIO_KEYS.bgmIntro;
    playBgm(this, endKey);

    const overlay = document.getElementById('end-overlay');
    const score = document.getElementById('end-score');
    const body = document.getElementById('end-body');
    overlay?.classList.remove('hidden');

    // Body copy: personalise with company if URL param supplied; otherwise
    // drop the "at X" tail entirely. params.company defaults to 'your
    // company' when no ?company= was passed — treat that sentinel as absent.
    if (body) {
      const company = data.params.company;
      const supplied = company && company !== 'your company';
      body.textContent = supplied
        ? `Discover and automatically protect your complete external attack surface at ${company}.`
        : 'Discover and automatically protect your complete external attack surface with IONIX.';
    }

    const time = fmtTime(elapsedSeconds(data.state));
    const name = data.params.name || 'Agent';
    const patched = data.state.patchedCount;
    const total = data.state.totalAssets;

    if (score) {
      score.innerHTML = '';
      const t1 = document.createTextNode('Nice run, ');
      const nameSpan = document.createElement('span');
      nameSpan.className = 'name';
      nameSpan.textContent = name;
      const t2 = document.createTextNode('. You patched ');
      const patchedSpan = document.createElement('span');
      patchedSpan.className = 'stat';
      patchedSpan.textContent = `${patched} of ${total}`;
      const t3 = document.createTextNode(' assets in ');
      const timeSpan = document.createElement('span');
      timeSpan.className = 'stat';
      timeSpan.textContent = time;
      const t4 = document.createTextNode(' before MYTHOS arrived.');
      score.append(t1, nameSpan, t2, patchedSpan, t3, timeSpan, t4);
    }

    track('cta_shown', { outcome: data.state.outcome, patched: data.state.patchedCount });

    const ctaPrimary = document.getElementById('cta-primary');
    const ctaReplay = document.getElementById('cta-replay');

    const onPrimary = () => {
      playSfx(this, AUDIO_KEYS.sfxClick);
      track('cta_clicked', { kind: 'primary', outcome: data.state.outcome });
    };
    const onReplay = () => {
      playSfx(this, AUDIO_KEYS.sfxClick);
      track('cta_clicked', { kind: 'replay' });
      overlay?.classList.add('hidden');
      this.scene.start('Intro', { params: data.params });
    };

    // AbortController cleans up all CTA listeners on scene shutdown — without
    // it, listeners that never fire (e.g. you replay without clicking the
    // primary CTA) accumulate across End → Intro → Game → End loops.
    const ac = new AbortController();
    ctaPrimary?.addEventListener('click', onPrimary, { once: true, signal: ac.signal });
    ctaReplay?.addEventListener('click', onReplay, { once: true, signal: ac.signal });

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      ac.abort();
      overlay?.classList.add('hidden');
    });
  }
}
